import { matchSiteStructure } from '../prompts/section.js';
import { ST, sectionId } from '../state.js';

// ── BUILD HELPERS ─────────────────────────────────────────────────
// Output cleaning, IIFE wrapping, stock image keywords, upload placeholders,
// section/page factory helpers.

// Pure utility functions — no DOM dependency. Direct migration to Vue utils.

/**
 * Initialize a section from concept data with build-state defaults.
 * @param {import('../types.ts').ConceptSection} s
 * @param {import('../types.ts').Section[]} [existing]
 * @returns {import('../types.ts').Section}
 */
const initSection = (s, existing = []) => ({
  ...s,
  id: sectionId(s.type, existing),
  html: '',
  status: 'pending',
  note: '',
  error: '',
  replaceImg: null,
  screenshotImg: null,
  editLog: [],
});

/**
 * Create a new page object with build-state defaults.
 * @param {string} name
 * @param {import('../types.ts').Section[]} sects
 * @returns {import('../types.ts').Page}
 */
const newPage = (name, sects) => ({
  id: `page-${name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')}-${Date.now()}`,
  name: name.trim(),
  sects,
  shellHtml: '',
  shellStatus: 'pending',
  assembled: '',
  editHist: [],
  editUndoStack: [],
  chatLog: [],
});

/**
 * Strip markdown fences from Claude output.
 * @param {string} raw
 * @returns {string}
 */
function cleanModelOutput(raw) {
  let html = raw
    .replace(/^```html?\s*\n?/i, '')
    .replace(/\n?```\s*$/, '')
    .trim();

  // Remove unclosed <script> blocks caused by AI output truncation.
  // An unclosed <script> is fatal: the HTML parser stays in "script data" state
  // and swallows all subsequent content (styles, sections) as script text.
  // CSS selectors like #id get parsed as JS private fields, breaking doc.write().
  const lastScriptOpen = html.lastIndexOf('<script');
  const lastScriptClose = html.lastIndexOf('</script>');
  if (lastScriptOpen >= 0 && lastScriptOpen > lastScriptClose) {
    html = html.substring(0, lastScriptOpen).trimEnd();
  }

  return html;
}

// ── STOCK IMAGE KEYWORDS ─────────────────────────────────────────
// Semantic keyword map for scoring stock images against section types.
// Used by searchStockImages() to assign images to the right sections.
const TYPE_KW = {
  hero: [
    'hero',
    'landing',
    'banner',
    'welcome',
    'startup',
    'business',
    'professional',
    'modern',
    'workspace',
  ],
  problem: [
    'challenge',
    'struggle',
    'pain',
    'frustration',
    'stress',
    'difficulty',
    'obstacle',
    'broken',
  ],
  solution: [
    'solution',
    'success',
    'achievement',
    'improve',
    'growth',
    'result',
    'transform',
    'progress',
  ],
  features: [
    'feature',
    'tool',
    'benefit',
    'technology',
    'innovation',
    'platform',
    'dashboard',
    'device',
  ],
  process: ['step', 'workflow', 'method', 'strategy', 'plan', 'roadmap', 'timeline', 'diagram'],
  'social-proof': [
    'testimonial',
    'review',
    'client',
    'customer',
    'partner',
    'trust',
    'logo',
    'brand',
  ],
  testimonials: [
    'testimonial',
    'review',
    'client',
    'customer',
    'feedback',
    'portrait',
    'headshot',
    'person',
  ],
  services: [
    'service',
    'consulting',
    'expertise',
    'professional',
    'work',
    'project',
    'delivery',
    'craft',
  ],
  about: [
    'team',
    'office',
    'people',
    'founder',
    'company',
    'workspace',
    'meeting',
    'culture',
    'group',
  ],
  'service-area': [
    'location',
    'area',
    'city',
    'map',
    'community',
    'neighborhood',
    'building',
    'street',
  ],
  contact: ['office', 'building', 'phone', 'communication', 'desk', 'support', 'handshake'],
  subscribe: ['newsletter', 'email', 'notification', 'inbox', 'letter', 'envelope', 'mail'],
  video: ['video', 'play', 'watch', 'media', 'screen', 'presentation', 'recording'],
  content: ['content', 'section', 'block', 'info', 'detail', 'overview', 'general'],
};

// Word-boundary keyword match — avoids "plan" matching "plant" / "airplane"
const KwRxCache = {};

function kwMatch(text, kw) {
  let rx = KwRxCache[kw];
  if (!rx) {
    rx = new RegExp(`\\b${kw}`, 'i');
    KwRxCache[kw] = rx;
  }
  return rx.test(text);
}

// ── UPLOAD IMAGE PLACEHOLDERS ────────────────────────────────────
// Build map of placeholder → data URL for uploaded images.
// Placeholders like __UPLOADED_1__ are used in AI prompts and stored in s.html.
// Actual data URLs are substituted at render/export time to keep prompts small.
/**
 * Build map of placeholder tokens to data URLs for uploaded images.
 * @returns {Record<string, string>}
 */
function buildUploadImgMap() {
  const map = {};
  if (ST.uploads?.images?.length) {
    ST.uploads.images
      .filter(i => i.analysis?.useDecision === 'use')
      .forEach((img, i) => {
        map[`__UPLOADED_${i + 1}__`] = `data:${img.type || 'image/jpeg'};base64,${img.data}`;
      });
  }
  if (ST.logoImg?.data && ST.logoImg.analysis?.useDecision !== 'skip') {
    map['__UPLOADED_LOGO__'] = `data:${ST.logoImg.type || 'image/png'};base64,${ST.logoImg.data}`;
  }
  // Per-section replace images (attached via chat "Use as image")
  for (const pg of ST.pages) {
    for (const s of pg.sects) {
      if (s.replaceImg?.data) {
        map[`__REPLACE_IMG_${s.id}__`] = `data:${s.replaceImg.type || 'image/jpeg'};base64,${s.replaceImg.data}`;
      }
    }
  }
  return map;
}

// Replace placeholder tokens with actual data URLs in HTML.
// Lazily builds the map if not cached — safe to call from any render path.
/**
 * Replace placeholder tokens with actual data URLs in HTML.
 * @param {string} html
 * @returns {string}
 */
function replaceUploadPlaceholders(html) {
  if (!html) {
    return html;
  }
  // Fast path: skip if HTML contains no placeholders
  if (html.indexOf('__UPLOADED_') === -1 && html.indexOf('__REPLACE_IMG_') === -1) {
    return html;
  }
  // Use cached map or build on demand
  const map = ST.uploadImgMap || buildUploadImgMap();
  for (const [key, dataUrl] of Object.entries(map)) {
    html = html.replaceAll(key, dataUrl);
  }
  return html;
}

// ── BRIEF HYDRATION ─────────────────────────────────────────────
// Schema uses pipe-delimited strings for services/staff/testimonials/stats
// to keep grammar small. This converts them back to structured objects.
const hydrateBrief = mb => {
  if (!mb?.facts) {
    return mb;
  }
  const f = mb.facts;
  const split = (s, ...keys) => {
    const parts = s.split('|').map(p => p.trim());
    const obj = {};
    keys.forEach((k, i) => {
      obj[k] = parts[i] || '';
    });
    return obj;
  };
  if (f.services?.[0] && typeof f.services[0] === 'string') {
    f.services = f.services.map(s => split(s, 'name', 'description', 'price'));
  }
  if (f.namedStaff?.[0] && typeof f.namedStaff[0] === 'string') {
    f.namedStaff = f.namedStaff.map(s => split(s, 'name', 'role'));
  }
  if (f.testimonials?.[0] && typeof f.testimonials[0] === 'string') {
    f.testimonials = f.testimonials.map(s => split(s, 'quote', 'author'));
  }
  if (f.stats?.[0] && typeof f.stats[0] === 'string') {
    f.stats = f.stats.map(s => split(s, 'number', 'label'));
  }
  return mb;
};

// ── SECTION EDIT UTILITIES (moved from preview.js) ──────────────

// Regex that matches a section ID as a CSS selector (not as substring of another ID)
const sectionIdPattern = sectionId => new RegExp(`#${sectionId}(?=[\\s{.,:\\[>~+}]|$)`);

// Find the <style> block scoped to a section ID within a parsed document
const findScopedStyle = (doc, sectionId) => {
  const styles = Array.from(doc.querySelectorAll('style:not([data-shell])'));
  const pat = sectionIdPattern(sectionId);
  return styles.find(s => pat.test(s.textContent)) ?? null;
};

// Extract a single section's HTML + its scoped <style> from the assembled page
const extractSectionHtml = (assembledHtml, sectionId) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(assembledHtml, 'text/html');
  const sectionEl = doc.getElementById(sectionId);
  if (!sectionEl) {
    return null;
  }
  const sectionStyle = findScopedStyle(doc, sectionId)?.outerHTML ?? '';
  return { sectionHtml: sectionEl.outerHTML, sectionStyle };
};

// Replace a section's HTML + style in the assembled page
const replaceSectionInAssembled = (assembledHtml, sectionId, newContent) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(assembledHtml, 'text/html');
  const oldSection = doc.getElementById(sectionId);
  if (!oldSection) {
    return assembledHtml;
  }

  const newDoc = parser.parseFromString(`<div>${newContent}</div>`, 'text/html');
  const newStyle = newDoc.querySelector('style');
  const newSection =
    newDoc.querySelector('section') ||
    newDoc.querySelector('header') ||
    newDoc.querySelector('footer') ||
    newDoc.querySelector(`#${sectionId}`);

  const oldStyle = findScopedStyle(doc, sectionId);
  if (oldStyle && newStyle) {
    oldStyle.replaceWith(newStyle);
  } else if (newStyle) {
    oldSection.parentNode.insertBefore(newStyle, oldSection);
  }

  if (newSection) {
    oldSection.replaceWith(newSection);
  }

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
};

// Build edit history block for section-scoped edits
const buildEditHistory = (sect, currentInstr) => {
  const prev = sect.editLog ?? [];
  if (!prev.length) {
    return '';
  }
  return `<edit_history>
${prev.map((h, i) => `${i + 1}. "${h.instr}" — applied${h.applied ? ` (${h.applied})` : ''}`).join('\n')}
${prev.length + 1}. "${currentInstr}" — current request
</edit_history>

Preserve ALL changes from previous edits. Apply only the current request.`;
};

// ── SAFE STREAMING HTML ─────────────��───────────────────────────
/**
 * Auto-close partial tags during streaming to prevent broken layout.
 * @param {string} html
 * @returns {string}
 */
const safeStreamHtml = html => {
  if (!html) {
    return html;
  }

  // 1. Truncate trailing partial tag: "<div class='he" → remove
  const lastLt = html.lastIndexOf('<');
  const lastGt = html.lastIndexOf('>');
  if (lastLt > lastGt) {
    html = html.substring(0, lastLt);
  }

  // 2a. Remove unclosed <script> blocks — truncated JS in script data state
  //     would swallow all subsequent HTML content during doc.write fallback.
  const lastScriptOpen = html.lastIndexOf('<script');
  const lastScriptClose = html.lastIndexOf('</script');
  if (lastScriptOpen >= 0 && lastScriptOpen > lastScriptClose) {
    html = html.substring(0, lastScriptOpen);
  }

  // 2b. Handle partial <style> content — truncate to last complete rule
  const lastStyleOpen = html.lastIndexOf('<style');
  const lastStyleClose = html.lastIndexOf('</style');
  if (lastStyleOpen > lastStyleClose) {
    const styleTagEnd = html.indexOf('>', lastStyleOpen);
    if (styleTagEnd >= 0) {
      const cssContent = html.substring(styleTagEnd + 1);
      const lastBrace = cssContent.lastIndexOf('}');
      if (lastBrace >= 0) {
        html = html.substring(0, styleTagEnd + 1 + lastBrace + 1);
      } else {
        html = html.substring(0, lastStyleOpen);
      }
    }
  }

  // 3. Auto-close remaining open tags
  const voidTags = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'source',
    'track',
    'wbr',
  ]);
  const stack = [];
  const tagRx = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*\/?>/g;
  let m;
  while ((m = tagRx.exec(html))) {
    const tag = m[1].toLowerCase();
    if (voidTags.has(tag)) {
      continue;
    }
    if (m[0].startsWith('</')) {
      const idx = stack.lastIndexOf(tag);
      if (idx >= 0) {
        stack.splice(idx, 1);
      }
    } else if (!m[0].endsWith('/>')) {
      stack.push(tag);
    }
  }

  return (
    html +
    stack
      .reverse()
      .map(t => `</${t}>`)
      .join('')
  );
};

// ── IMAGE FETCH + COLLECT ───────────────────────────────────────
// Fetch external images via img-proxy, resize client-side, cache by URL.
// Used by collectSectionImages() to pass vision-ready images to claude().

/** @type {Map<string, Promise<import('../types.ts').Base64ImageInput | null>>} */
const ImgFetchCache = new Map();

/**
 * Fetch an image via img-proxy, resize to maxWidth, return as base64.
 * Cached by URL to avoid duplicate fetches across sections.
 * @param {string} url - original image URL (will be proxied)
 * @param {string} label
 * @param {number} [maxWidth=600]
 * @returns {Promise<import('../types.ts').Base64ImageInput | null>}
 */
const fetchImgBase64 = (url, label, maxWidth = 600) => {
  const proxyUrl = `/api/img-proxy?src=${encodeURIComponent(url)}`;

  if (!ImgFetchCache.has(url)) {
    ImgFetchCache.set(
      url,
      (async () => {
        try {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 5000);
          const resp = await fetch(`${location.origin}${proxyUrl}`, { signal: ctrl.signal });
          clearTimeout(timer);
          if (!resp.ok) {
            return null;
          }
          const blob = await resp.blob();
          if (!blob.type?.startsWith('image/')) {
            return null;
          }
          const bitmap = await createImageBitmap(blob);
          let w = bitmap.width,
            h = bitmap.height;
          if (w > maxWidth) {
            const scale = maxWidth / w;
            h = Math.round(h * scale);
            w = maxWidth;
          }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
          bitmap.close();
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          return { data: dataUrl.replace(/^data:[^;]+;base64,/, ''), type: 'image/jpeg' };
        } catch {
          return null;
        }
      })(),
    );
  }

  return ImgFetchCache.get(url).then(r => (r ? { ...r, label } : null));
};

/** Clear fetch cache between builds */
const clearImgFetchCache = () => ImgFetchCache.clear();

/**
 * Collect vision-ready images for a section build.
 * - Stock images: URL source (srcSmall ~400px, already tiny, ~142 tokens each)
 * - Existing/poster images: fetch + resize to 600px (~320 tokens each)
 *
 * @param {Object} section - {id, type, videoEmbed, _structOrder, ...}
 * @param {Object} [opts]
 * @param {Object} [opts.pg] - page object (for header-hero coordination)
 * @param {number} [opts.maxImages=3]
 * @returns {Promise<Array<import('../types.ts').ImageInput>>}
 */
const collectSectionImages = async (section, opts = {}) => {
  const { pg, maxImages = 3 } = opts;
  const urlImgs = []; // URL source (stock — already small)
  const fetchImgs = []; // need fetch+resize (existing, posters)
  const seen = new Set();

  const addUrl = (url, label) => {
    if (!url?.startsWith('http') || seen.has(url)) {
      return;
    }
    seen.add(url);
    urlImgs.push({ url, label });
  };

  const addFetch = (url, label) => {
    if (!url?.startsWith('http') || seen.has(url)) {
      return;
    }
    seen.add(url);
    fetchImgs.push({ url, label });
  };

  // 1. siteStructure per-section images → fetch+resize (unknown size)
  const structEntry = matchSiteStructure(section);
  if (structEntry?.images?.length) {
    for (const img of structEntry.images) {
      addFetch(
        img.url,
        `Available section image (${img.position || 'unknown'}): ${img.description || 'existing site image'}`,
      );
    }
  }

  // 2. Marketing brief images by category → fetch+resize (unknown size)
  const mb = ST.marketingBrief;
  const existingImages = (mb?.images || []).filter(i => i.src?.startsWith('http'));
  const typeAliases = {
    hero: ['hero', 'banner', 'background', 'main'],
    about: ['about', 'team', 'company', 'office'],
    services: ['services', 'service', 'product', 'work'],
    testimonials: ['testimonial', 'client', 'review'],
    cta: ['hero', 'banner', 'background'],
  };
  const matchCats = typeAliases[section.type] || [section.type];
  for (const img of existingImages) {
    const cat = (img.category || '').toLowerCase();
    if (matchCats.some(alias => cat === alias)) {
      addFetch(img.src, `Available image: ${img.alt || 'site image'}${cat ? ` [${cat}]` : ''}`);
    }
  }

  // 3. Stock images → URL source with srcSmall (~400px thumb, ~142 tokens)
  const sectionStock = (ST.stockImages || []).filter(
    i => !i.excluded && (i.forSection === section.id || i.forSection === '_general'),
  );
  for (const img of sectionStock) {
    addUrl(
      img.srcSmall || img.srcMedium || img.src,
      `Available stock image: ${img.alt || 'stock photo'}`,
    );
  }

  // 4. Video poster → fetch+resize (unknown size)
  if (section.videoEmbed?.thumbnailUrl?.startsWith('http')) {
    addFetch(
      section.videoEmbed.thumbnailUrl,
      'Video poster/thumbnail — initial visual before video plays. Analyze contrast for text readability.',
    );
  }

  // 5. Header-hero coordination
  if (section.type === 'header' && pg) {
    const heroSect = pg.sects.find(x => x.type === 'hero');
    if (heroSect) {
      const heroStruct = matchSiteStructure(heroSect);
      if (heroStruct?.images?.length) {
        for (const img of heroStruct.images) {
          addFetch(
            img.url,
            `Hero background (header overlays hero) — ensure header text readable: ${img.description || 'hero image'}`,
          );
        }
      }
      const heroStock = (ST.stockImages || []).filter(
        i => !i.excluded && i.forSection === heroSect.id,
      );
      for (const img of heroStock) {
        addUrl(
          img.srcSmall || img.srcMedium || img.src,
          `Hero background (header overlays hero): ${img.alt || 'hero stock'}`,
        );
      }
      if (heroSect.videoEmbed?.thumbnailUrl?.startsWith('http')) {
        addFetch(
          heroSect.videoEmbed.thumbnailUrl,
          'Hero video poster (header overlays hero) — ensure header text readable',
        );
      }
    }
  }

  // Cap total, prioritize what we already have (URLs) before fetch work
  const cap = maxImages;
  const result = [...urlImgs.slice(0, cap)];
  const fetchSlots = cap - result.length;
  if (fetchSlots > 0 && fetchImgs.length) {
    const fetched = await Promise.all(
      fetchImgs.slice(0, fetchSlots).map(f => fetchImgBase64(f.url, f.label)),
    );
    result.push(...fetched.filter(Boolean));
  }

  return result;
};

/**
 * Resolve the best available logo as a vision-ready image input.
 * Priority: uploaded file > pasted URL > scraped from existing site > marketing brief URL.
 * @param {string} [label] - Vision label for the image
 * @returns {import('../types.ts').Base64ImageInput | import('../types.ts').UrlImageInput | null}
 */
const getLogoVisionInput = (label = 'Company logo') => {
  if (ST.logoImg?.data && ST.logoImg.analysis?.useDecision !== 'skip') {
    return { ...ST.logoImg, label };
  }
  if (ST.logoUrl?.trim()) {
    return { url: ST.logoUrl.trim(), label };
  }
  const scraped = ST.existingScrape?.logos?.find(l => l.src);
  if (scraped) {
    return { url: scraped.src, label };
  }
  if (ST.marketingBrief?.logoUrl?.trim() && /^https?:\/\//.test(ST.marketingBrief.logoUrl)) {
    return { url: ST.marketingBrief.logoUrl, label };
  }
  return null;
};

// ── SHARED SCRAPE + SCREENSHOT HELPERS ──────────────────────────
// Extracted from screens/existing.js for reuse in page-analyzer.js.

/**
 * Format scrape result as XML-tagged user message for AI analysis.
 * @param {import('../types.ts').SiteScrape | import('../types.ts').PageScrapeData} data
 * @returns {string}
 */
const buildScrapeMessage = data => {
  const parts = [`Base URL: ${data.url}`, `Page Title: ${data.title || ''}`];
  if (data.metaDescription) {
    parts.push(`Meta: ${data.metaDescription}`);
  }
  if (data.ldJson?.length) {
    parts.push(`<structured_data>\n${JSON.stringify(data.ldJson, null, 2)}\n</structured_data>`);
  }
  if (data.css) {
    parts.push(`<extracted_css>\n${data.css}\n</extracted_css>`);
  }
  if (data.copyright) {
    parts.push(`<copyright>${data.copyright}</copyright>`);
  }
  if (data.videos?.length) {
    const vidLines = data.videos.map(v => {
      if (v.type === 'youtube') {
        return `YouTube: https://youtube.com/watch?v=${v.id} (poster: ${v.poster})`;
      }
      if (v.type === 'vimeo') {
        return `Vimeo: https://vimeo.com/${v.id}`;
      }
      return `Hosted: ${v.sources?.[0] || 'unknown'}${v.poster ? ` (poster: ${v.poster})` : ''}`;
    });
    parts.push(`<videos>\n${vidLines.join('\n')}\n</videos>`);
  }
  if (data.rawHtml) {
    parts.push(`<page_html>\n${data.rawHtml}\n</page_html>`);
  }
  return parts.join('\n\n');
};

/**
 * Convert a screenshot blob into base64 tile array (max 7900px per tile).
 * @param {Blob} blob
 * @returns {Promise<import('../types.ts').UploadedImage[] | null>}
 */
const blobToTiles = blob => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const MAX = 7900;
      const w = img.naturalWidth;
      const fullH = img.naturalHeight;
      const tiles = [];
      for (let y = 0; y < fullH; y += MAX) {
        const h = Math.min(MAX, fullH - y);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, y, w, h, 0, 0, w, h);
        const dataUri = canvas.toDataURL('image/jpeg', 1);
        tiles.push({
          data: dataUri.replace(/^data:[^;]+;base64,/, ''),
          type: 'image/jpeg',
          name: `screenshot${tiles.length > 0 ? `-${tiles.length + 1}` : ''}.jpg`,
          preview: tiles.length === 0 ? dataUri : undefined,
        });
      }
      URL.revokeObjectURL(img.src);
      resolve(tiles);
    };
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(blob);
  });
};

/**
 * Add labels to screenshot tiles for AI vision input.
 * @param {import('../types.ts').UploadedImage[] | null} tiles
 * @param {string} url - source URL for labeling
 * @returns {Array<import('../types.ts').Base64ImageInput> | null}
 */
const labelTiles = (tiles, url) => {
  if (!tiles?.length) {
    return null;
  }
  return tiles.map((tile, i) => ({
    ...tile,
    label:
      i === 0
        ? `Full-page screenshot of ${url} — analyze visual identity, layout, content structure`
        : `(screenshot tile ${i + 1})`,
  }));
};

/** Strip HTML tags, style/script blocks → plain text, trimmed to maxLen. */
const stripHtmlToText = (html, maxLen = 200) => {
  if (!html) {
    return '';
  }
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
};

// ── MODULE EXPORTS ──────
export {
  blobToTiles,
  buildEditHistory,
  buildScrapeMessage,
  buildUploadImgMap,
  cleanModelOutput,
  clearImgFetchCache,
  collectSectionImages,
  extractSectionHtml,
  findScopedStyle,
  getLogoVisionInput,
  hydrateBrief,
  initSection,
  kwMatch,
  labelTiles,
  newPage,
  replaceSectionInAssembled,
  replaceUploadPlaceholders,
  safeStreamHtml,
  sectionIdPattern,
  stripHtmlToText,
  TYPE_KW,
};
